/**
 * MOCK ROBOT SIMULATOR (ESP32 NODEMCU - RETOCAR-STARTER)
 * Giả lập bo mạch ESP32 và các cảm biến/cơ cấu ngoại vi thời gian thực cho RETOBLOCK Studio.
 * Cho phép kiểm thử 100% chức năng truyền thông, nạp code, giám sát telemetry và ngắt STOP mà không cần xe thật.
 */

import {
  BLE_CONFIG,
  COMMANDS,
  buildFrame,
  calculateCRC16,
  calculateCRC32,
  parseFrame,
  classifyColorRgb
} from './ble-protocol.js';

class AudioFeedbackHelper {
  constructor() {
    this.ctx = null;
  }

  init() {
    if (typeof window !== 'undefined' && !this.ctx && (window.AudioContext || window.webkitAudioContext)) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      this.ctx = new AudioCtx();
    }
  }

  playBeep(freq = 1000, durationMs = 60) {
    try {
      this.init();
      if (!this.ctx) return;
      if (this.ctx.state === 'suspended') {
        this.ctx.resume();
      }
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'square';
      osc.frequency.setValueAtTime(freq, this.ctx.currentTime);
      gain.gain.setValueAtTime(0.1, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + durationMs / 1000);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + durationMs / 1000);
    } catch (e) {
      console.log('Audio feedback not allowed or disabled:', e);
    }
  }
}

export class MockRobotServer {
  constructor() {
    this.name = 'RETOCAR_STARTER_MOCK';
    this.isConnected = false;
    this.audio = new AudioFeedbackHelper();

    // Robot Telemetry State
    this.distance = 45;
    this.distanceDir = -1;
    this.lineSensors = { s1: false, s2: true, s3: false };
    this.lightLevel = 68;
    this.colorId = 1; // 1: RED
    this.rgb = { r: 240, g: 30, b: 30 };
    this.buttons = { bt1: false, bt2: false };
    this.robotState = 0; // 0: IDLE, 1: RUNNING

    // RAM Buffer for Chunking
    this.ramBuffer = [];
    this.expectedChunks = 0;
    this.expectedSize = 0;

    // BLE Fast OTA State
    this.isOtaActive = false;
    this.otaFileSize = 0;
    this.otaExpectedCrc32 = 0;
    this.otaReceivedBytes = 0;
    this.otaExpectedChunkIdx = 0;
    this.otaChunks = [];

    // Timer & Notification Callback
    this.telemetryInterval = null;
    this.notifyCallback = null;
    this.onStateChangeCallback = null;
    this.onRgbChangeCallback = null;
    this.cssColor = 'rgb(240, 30, 30)';
    this.executionTimer = null;
    this.watchdogTimer = null;
    this.lastPacketTime = Date.now();
  }

  setRgbChangeCallback(callback) {
    this.onRgbChangeCallback = callback;
  }

  setRgbColor(r = 0, g = 0, b = 0) {
    const clamp = (val) => Math.max(0, Math.min(255, Math.round(Number(val) || 0)));
    this.rgb = {
      r: clamp(r),
      g: clamp(g),
      b: clamp(b)
    };
    this.colorId = classifyColorRgb(this.rgb.r, this.rgb.g, this.rgb.b);
    this.cssColor = `rgb(${this.rgb.r}, ${this.rgb.g}, ${this.rgb.b})`;

    // Phát tín hiệu thay đổi màu cho giao diện xe ảo
    if (this.onRgbChangeCallback) {
      try { this.onRgbChangeCallback(this.rgb, this.cssColor); } catch (e) { /* ignore */ }
    }
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      try {
        window.dispatchEvent(new CustomEvent('mock-robot:rgb-changed', {
          detail: { rgb: this.rgb, cssColor: this.cssColor }
        }));
      } catch (e) { /* ignore */ }
    }

    // Gửi telemetry để các thành phần UI lắng nghe cập nhật ngay lập tức
    if (this.isConnected) {
      this.sendTelemetryPacket();
    }
    return this.cssColor;
  }

  setRgbNamedColor(name) {
    if (!name || name === 'OFF' || name === 'BLACK') {
      return this.setRgbColor(0, 0, 0);
    }
    const colorMap = {
      RED: [255, 0, 0],
      GREEN: [0, 255, 0],
      BLUE: [0, 0, 255],
      YELLOW: [255, 255, 0],
      WHITE: [255, 255, 255],
      PURPLE: [160, 32, 240],
      BROWN: [165, 42, 42],
      CYAN: [0, 255, 255],
      MAGENTA: [255, 0, 255]
    };
    const c = colorMap[String(name).toUpperCase()] || [255, 255, 255];
    return this.setRgbColor(c[0], c[1], c[2]);
  }

  setRgbPercent(rPct = 0, gPct = 0, bPct = 0) {
    const clampPct = (val) => Math.max(0, Math.min(100, Number(val) || 0));
    const r = Math.round((clampPct(rPct) * 255) / 100);
    const g = Math.round((clampPct(gPct) * 255) / 100);
    const b = Math.round((clampPct(bPct) * 255) / 100);
    return this.setRgbColor(r, g, b);
  }

  setNotifyCallback(callback) {
    this.notifyCallback = callback;
  }

  setStateChangeCallback(callback) {
    this.onStateChangeCallback = callback;
  }

  connect() {
    this.isConnected = true;
    this.lastPacketTime = Date.now();
    this.startTelemetry();
    this.startWatchdog();
    if (this.onStateChangeCallback) this.onStateChangeCallback('connected');
  }

  disconnect() {
    this.isConnected = false;
    this.stopTelemetry();
    this.stopWatchdog();
    this.emergencyStop();
    if (this.onStateChangeCallback) this.onStateChangeCallback('disconnected');
  }

  startTelemetry() {
    if (this.telemetryInterval) clearInterval(this.telemetryInterval);
    // 10Hz = 100ms
    this.telemetryInterval = setInterval(() => {
      if (!this.isConnected) return;
      this.updateSimulationPhysics();
      this.sendTelemetryPacket();
    }, 100);
  }

  stopTelemetry() {
    if (this.telemetryInterval) {
      clearInterval(this.telemetryInterval);
      this.telemetryInterval = null;
    }
  }

  startWatchdog() {
    if (this.watchdogTimer) clearInterval(this.watchdogTimer);
    // Kiểm tra timeout 500ms
    this.watchdogTimer = setInterval(() => {
      if (!this.isConnected) return;
      if (Date.now() - this.lastPacketTime > 600 && this.robotState === 1) {
        console.warn('[MOCK ROBOT WATCHDOG] Fail-Safe Activated: Mất tín hiệu > 500ms');
        this.emergencyStop();
      }
    }, 200);
  }

  stopWatchdog() {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  resetWatchdog() {
    this.lastPacketTime = Date.now();
  }

  updateSimulationPhysics() {
    // Mô phỏng cảm biến siêu âm chuyển động mượt
    if (this.robotState === 1) {
      this.distance += this.distanceDir * 2;
      if (this.distance <= 12) {
        this.distance = 12;
        this.distanceDir = 1;
        // Mô phỏng gặp vật cản thì mắt line đổi
        this.lineSensors = { s1: true, s2: true, s3: false };
      } else if (this.distance >= 55) {
        this.distance = 55;
        this.distanceDir = -1;
        this.lineSensors = { s1: false, s2: true, s3: false };
      }
      // Mô phỏng đổi màu luân phiên theo 8 màu chuẩn STEM
      if (this.distance === 20) {
        this.rgb = { r: 25, g: 50, b: 230 }; // BLUE
        this.colorId = classifyColorRgb(this.rgb.r, this.rgb.g, this.rgb.b); // 3
      } else if (this.distance === 30) {
        this.rgb = { r: 160, g: 30, b: 200 }; // PURPLE
        this.colorId = classifyColorRgb(this.rgb.r, this.rgb.g, this.rgb.b); // 8
      } else if (this.distance === 40) {
        this.rgb = { r: 235, g: 30, b: 35 }; // RED
        this.colorId = classifyColorRgb(this.rgb.r, this.rgb.g, this.rgb.b); // 1
      }
    } else {
      // Khi IDLE: dao động nhẹ ±1 cm mô phỏng nhiễu thực tế
      this.distance = Math.max(5, Math.min(60, this.distance + (Math.random() > 0.5 ? 0.2 : -0.2)));
    }

    // Quang trở dao động nhẹ
    this.lightLevel = Math.max(50, Math.min(85, Math.round(68 + Math.sin(Date.now() / 2000) * 8)));
  }

  sendTelemetryPacket() {
    if (!this.notifyCallback) return;

    // Payload 10 bytes:
    // Byte 0-1: Dist (uint16_t BE)
    // Byte 2: Line bits (s1=bit 0, s2=bit 1, s3=bit 2)
    // Byte 3: Light (0-100)
    // Byte 4: Color ID
    // Byte 5-7: R, G, B
    // Byte 8: Buttons
    // Byte 9: State (0: IDLE, 1: RUNNING)
    const payload = new Uint8Array(10);
    const distInt = Math.round(this.distance);
    payload[0] = (distInt >> 8) & 0xFF;
    payload[1] = distInt & 0xFF;

    let lineBits = 0;
    if (this.lineSensors.s1) lineBits |= 0b001;
    if (this.lineSensors.s2) lineBits |= 0b010;
    if (this.lineSensors.s3) lineBits |= 0b100;
    payload[2] = lineBits;

    payload[3] = this.lightLevel & 0xFF;
    payload[4] = this.colorId & 0xFF;
    payload[5] = this.rgb.r & 0xFF;
    payload[6] = this.rgb.g & 0xFF;
    payload[7] = this.rgb.b & 0xFF;

    let btnBits = 0;
    if (this.buttons.bt1) btnBits |= 0b01;
    if (this.buttons.bt2) btnBits |= 0b10;
    payload[8] = btnBits;

    payload[9] = this.robotState & 0xFF;

    const frame = buildFrame(COMMANDS.TELEMETRY, payload);
    this.notifyCallback(frame);
  }

  handleIncomingData(frameBytes) {
    this.resetWatchdog();
    const frame = parseFrame(frameBytes);
    if (!frame) return;
    const { cmdId, payload } = frame;

    switch (cmdId) {
      case COMMANDS.HEARTBEAT:
        // Reset watchdog, nothing more needed
        break;

      case COMMANDS.PING_IDENTIFY:
        this.handlePing();
        break;

      case COMMANDS.FILE_START:
        this.handleFileStart(payload);
        break;

      case COMMANDS.FILE_DATA:
        this.handleFileData(payload);
        break;

      case COMMANDS.FILE_END:
        this.handleFileEnd(payload);
        break;

      case COMMANDS.EMERGENCY_STOP:
        this.emergencyStop();
        break;

      case COMMANDS.OTA_BEGIN:
        this.handleOtaBegin(payload);
        break;

      case COMMANDS.OTA_DATA:
        this.handleOtaData(payload);
        break;

      case COMMANDS.OTA_END:
        this.handleOtaEnd(payload);
        break;

      case COMMANDS.OTA_ABORT:
        this.handleOtaAbort(payload);
        break;

      case COMMANDS.GET_LOGS:
        this.handleGetLogs();
        break;

      default:
        console.log(`[MOCK ROBOT] Unknown command: 0x${cmdId.toString(16)}`);
    }
  }

  handleGetLogs() {
    console.log('[MOCK ROBOT] GET_LOGS received, streaming recent 5-min RAM logs...');
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const timeStr = `${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    const mockLogs = [
      `[${timeStr}] [INFO] [SYS] RETOLOGIX v1.1.14 booted successfully (Dual-Core ESP32)`,
      `[${timeStr}] [INFO] [BLE] Client connected (handle=1)`,
      `[${timeStr}] [INFO] [I2C] Ultrasonic RCWL-9200 optimized (max 50cm, cycle 35ms)`,
      `[${timeStr}] [INFO] [I2C] Line Tracker 3-channel initialized at 100Hz`,
      `[${timeStr}] [INFO] [LOGS] Host requested logs stream from ESP32 RAM buffer`
    ].join('\n') + '\n';

    const textEncoder = new TextEncoder();
    const logBytes = textEncoder.encode(mockLogs);
    const chunkSize = 120;
    const totalChunks = Math.ceil(logBytes.length / chunkSize) || 1;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, logBytes.length);
      const isLast = (i === totalChunks - 1) ? 1 : 0;
      const chunkData = logBytes.slice(start, end);

      const payload = new Uint8Array(2 + chunkData.length);
      payload[0] = i;
      payload[1] = isLast;
      payload.set(chunkData, 2);

      const frame = buildFrame(COMMANDS.LOG_DATA, payload);
      setTimeout(() => {
        if (this.notifyCallback) {
          this.notifyCallback(frame);
        }
      }, i * 20);
    }
  }

  handlePing() {
    console.log('[MOCK ROBOT] PING IDENTIFY: Buzzer beep & 3x RGB flash');
    this.audio.playBeep(1800, 80);
    // Báo hiệu trực quan nếu cần
  }

  handleFileStart(payload) {
    this.expectedSize = (payload[0] << 8) | payload[1];
    this.expectedChunks = (payload[2] << 8) | payload[3];
    this.ramBuffer = [];
    console.log(`[MOCK ROBOT] FILE_START: Expected ${this.expectedSize} bytes in ${this.expectedChunks} chunks`);
  }

  handleFileData(payload) {
    const chunkIdx = (payload[0] << 8) | payload[1];
    const chunkBytes = payload.slice(2);
    this.ramBuffer[chunkIdx] = chunkBytes;
  }

  handleFileEnd(payload) {
    const expectedCrc = (payload[0] << 8) | payload[1];

    // Ghép toàn bộ RAM buffer
    let totalLen = 0;
    for (const chunk of this.ramBuffer) {
      if (chunk) totalLen += chunk.length;
    }
    const combinedBytes = new Uint8Array(totalLen);
    let offset = 0;
    for (const chunk of this.ramBuffer) {
      if (chunk) {
        combinedBytes.set(chunk, offset);
        offset += chunk.length;
      }
    }

    const actualCrc = calculateCRC16(combinedBytes);
    const decoder = new TextDecoder('utf-8');
    const pythonCode = decoder.decode(combinedBytes);

    console.log(`[MOCK ROBOT] FILE_END: CRC Check expected=0x${expectedCrc.toString(16)}, actual=0x${actualCrc.toString(16)}`);

    if (expectedCrc === actualCrc || expectedCrc === 0) {
      // Nạp thành công: Còi phát 1 tiếng bíp ngắn (50ms) và kích hoạt Core 1
      this.audio.playBeep(2000, 70);
      this.robotState = 1; // RUNNING
      console.log('[MOCK ROBOT] Code loaded into Core 1 MicroPython VM. Starting execution...');

      // Mô phỏng hiệu ứng màu sắc LED RGB nếu mã Python có lệnh set_rgb_color
      const rgbValuesMatch = pythonCode.match(/robot\.set_rgb_color\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
      const rgbNamedMatch = pythonCode.match(/robot\.set_rgb_color\(\s*["']([^"']+)["']\s*\)/);
      if (rgbValuesMatch) {
        const r = parseInt(rgbValuesMatch[1], 10);
        const g = parseInt(rgbValuesMatch[2], 10);
        const b = parseInt(rgbValuesMatch[3], 10);
        this.setRgbColor(r, g, b);
      } else if (rgbNamedMatch) {
        this.setRgbNamedColor(rgbNamedMatch[1]);
      }

      // Kiểm tra xem code có lỗi cố ý để test TC-EDGE-03 không
      if (pythonCode.includes('1 / 0') || pythonCode.includes('ZeroDivisionError')) {
        setTimeout(() => {
          this.emitRuntimeError(0x02, 10, 'ZeroDivisionError: division by zero');
        }, 1200);
      } else if (pythonCode.includes('IndentationError')) {
        setTimeout(() => {
          this.emitRuntimeError(0x01, 7, 'IndentationError: unexpected indent');
        }, 800);
      } else if (!pythonCode.includes('while True:') && !pythonCode.includes('robot.stop()')) {
        // Nếu script chạy theo kịch bản hữu hạn (không loop vô hạn)
        if (this.executionTimer) clearTimeout(this.executionTimer);
        this.executionTimer = setTimeout(() => {
          console.log('[MOCK ROBOT] Script finished naturally. Moving state back to IDLE.');
          this.robotState = 0; // Trở về IDLE
        }, 4000);
      }
    } else {
      console.error('[MOCK ROBOT] CRC Mismatch! File rejected.');
      this.emergencyStop();
    }
  }

  emergencyStop() {
    console.log('[MOCK ROBOT] EMERGENCY STOP (0xFF): PWM=0%, Buzzer Off, Core 1 stopped');
    this.robotState = 0; // Chuyển về IDLE ngay lập tức
    if (this.executionTimer) {
      clearTimeout(this.executionTimer);
      this.executionTimer = null;
    }
    // Gửi ngay telemetry cập nhật trạng thái IDLE
    this.sendTelemetryPacket();
  }


  emitRuntimeError(errorCode, lineNum, message) {
    console.warn(`[MOCK ROBOT ERROR] Code=${errorCode}, Line=${lineNum}: ${message}`);
    this.emergencyStop();

    const encoder = new TextEncoder();
    const msgBytes = encoder.encode(message);
    const payload = new Uint8Array(3 + msgBytes.length);
    payload[0] = errorCode & 0xFF;
    payload[1] = (lineNum >> 8) & 0xFF;
    payload[2] = lineNum & 0xFF;
    payload.set(msgBytes, 3);

    const errorFrame = buildFrame(COMMANDS.RUNTIME_ERROR, payload);
    if (this.notifyCallback) {
      this.notifyCallback(errorFrame);
    }
  }

  // ==============================================================================
  // BLE FAST OTA SIMULATION HANDLERS (CMD 0x30 - 0x35)
  // ==============================================================================

  handleOtaBegin(payload) {
    if (payload.length < 8) return;
    this.otaFileSize = ((payload[0] << 24) | (payload[1] << 16) | (payload[2] << 8) | payload[3]) >>> 0;
    this.otaExpectedCrc32 = ((payload[4] << 24) | (payload[5] << 16) | (payload[6] << 8) | payload[7]) >>> 0;
    this.isOtaActive = true;
    this.otaReceivedBytes = 0;
    this.otaExpectedChunkIdx = 0;
    this.otaChunks = [];

    console.log(`[MOCK ROBOT OTA] Begin OTA: Size=${this.otaFileSize}B, CRC32=0x${this.otaExpectedCrc32.toString(16)}`);

    // Ngắt an toàn theo tài liệu: Ngắt PWM động cơ, tắt còi, dừng Core 1
    this.emergencyStop();

    // Phát âm thanh báo hiệu bắt đầu nạp OTA
    this.audio.playBeep(1500, 80);

    // Phản hồi CMD_OTA_READY (0x31): [Status=0x00, ChunkSize=480 (uint16_t BE)]
    const chunkSize = 480;
    const readyPayload = new Uint8Array([0x00, (chunkSize >> 8) & 0xFF, chunkSize & 0xFF]);
    const readyFrame = buildFrame(COMMANDS.OTA_READY, readyPayload);
    if (this.notifyCallback) {
      setTimeout(() => this.notifyCallback(readyFrame), 20);
    }
  }

  handleOtaData(payload) {
    if (!this.isOtaActive || payload.length < 2) return;
    const chunkIdx = (payload[0] << 8) | payload[1];
    const chunkBytes = payload.slice(2);

    if (chunkIdx !== this.otaExpectedChunkIdx) {
      console.error(`[MOCK ROBOT OTA] Chunk out of order: got #${chunkIdx}, expected #${this.otaExpectedChunkIdx}`);
      this.handleOtaAbort(new Uint8Array([0x03]));
      return;
    }

    this.otaChunks.push(chunkBytes);
    this.otaReceivedBytes += chunkBytes.length;
    this.otaExpectedChunkIdx++;

    // Giả lập cơ chế Flow Control: gửi OTA_ACK xác nhận mỗi 32 chunks
    if (chunkIdx > 0 && (chunkIdx % 32 === 0)) {
      const ackPayload = new Uint8Array([(chunkIdx >> 8) & 0xFF, chunkIdx & 0xFF]);
      const ackFrame = buildFrame(COMMANDS.OTA_ACK, ackPayload);
      if (this.notifyCallback) {
        setTimeout(() => this.notifyCallback(ackFrame), 2);
      }
    }
  }

  handleOtaEnd(payload) {
    if (!this.isOtaActive || payload.length < 4) return;
    const endCrc32 = ((payload[0] << 24) | (payload[1] << 16) | (payload[2] << 8) | payload[3]) >>> 0;

    // Gộp tất cả các chunks nhị phân
    const combinedBytes = new Uint8Array(this.otaReceivedBytes);
    let offset = 0;
    for (const chunk of this.otaChunks) {
      combinedBytes.set(chunk, offset);
      offset += chunk.length;
    }

    const actualCrc32 = calculateCRC32(combinedBytes);
    console.log(`[MOCK ROBOT OTA] End OTA: Expected CRC=0x${endCrc32.toString(16)}, Actual CRC=0x${actualCrc32.toString(16)} (Total: ${this.otaReceivedBytes} bytes)`);

    const isMatch = (endCrc32 === actualCrc32) || (this.otaExpectedCrc32 === actualCrc32);

    if (isMatch) {
      console.log('[MOCK ROBOT OTA] Firmware integrity verified 100%! Committing to Flash...');
      this.audio.playBeep(2400, 200); // 1 hồi còi dài 0.2s theo đặc tả

      // Phản hồi CMD_OTA_RESULT (0x34): [ResultCode=0x00, RebootCountdown=1s]
      const resultPayload = new Uint8Array([0x00, 0x01]);
      const resultFrame = buildFrame(COMMANDS.OTA_RESULT, resultPayload);
      if (this.notifyCallback) {
        this.notifyCallback(resultFrame);
      }

      // Giả lập vi điều khiển Soft-Reboot sau 1 giây
      setTimeout(() => {
        console.log('[MOCK ROBOT OTA] ESP32 Soft Reboot completed! Booting into new firmware...');
        this.isOtaActive = false;
        this.otaChunks = [];
        this.robotState = 0; // IDLE
      }, 1000);
    } else {
      console.error('[MOCK ROBOT OTA] CRC-32 Mismatch! Rejecting firmware.');
      const resultPayload = new Uint8Array([0x01, 0x00]); // Status=0x01 (CRC Error)
      const resultFrame = buildFrame(COMMANDS.OTA_RESULT, resultPayload);
      if (this.notifyCallback) {
        this.notifyCallback(resultFrame);
      }
      this.isOtaActive = false;
    }
  }

  handleOtaAbort(payload) {
    const reasonCode = payload && payload.length > 0 ? payload[0] : 0x02;
    console.warn(`[MOCK ROBOT OTA] Aborting OTA session. ReasonCode=0x${reasonCode.toString(16)}`);
    this.isOtaActive = false;
    this.otaChunks = [];
    this.otaReceivedBytes = 0;

    const abortPayload = new Uint8Array([reasonCode]);
    const abortFrame = buildFrame(COMMANDS.OTA_ABORT, abortPayload);
    if (this.notifyCallback) {
      this.notifyCallback(abortFrame);
    }
  }
}
